/**
 * Expiration & Deadline Radar Component
 * 
 * The "paralegal painkiller" - tracks and displays:
 * - Passport expiry
 * - I-94 expiry
 * - Visa expiry
 * - EAD/AP expiry
 * - Medical exam validity
 * - Filing deadlines
 * - And more...
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from '../../../auth/AuthenticatedAxios';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';
import { getCategoryDeadlineTypes } from '../../../Data/deadlineTypeCatalog';
import { 
  FaPassport, FaPlane, FaBriefcase, FaCalendarAlt, FaFileAlt, 
  FaHospital, FaMoneyBillAlt, FaGlobe, FaClock, FaEnvelope, 
  FaGavel, FaFingerprint, FaMicrophone, FaMapPin, FaIdCard, FaClipboardList
} from 'react-icons/fa';

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  color: #e2e8f0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(15, 23, 42, 0.8);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const HeaderTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #f1f5f9;
  
  svg {
    width: 22px;
    height: 22px;
    color: #f59e0b;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      transform: translateY(-1px);
    }
  ` : `
    background: rgba(51, 65, 85, 0.5);
    color: #94a3b8;
    border: 1px solid rgba(148, 163, 184, 0.2);
    
    &:hover {
      background: rgba(71, 85, 105, 0.5);
      color: #e2e8f0;
    }
  `}
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SummaryBar = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  background: rgba(30, 41, 59, 0.5);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  overflow-x: auto;
`;

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 16px;
  background: ${props => props.$bgColor || 'rgba(51, 65, 85, 0.3)'};
  border: 1px solid ${props => props.$borderColor || 'rgba(148, 163, 184, 0.1)'};
  border-radius: 8px;
  min-width: 80px;
  
  .count {
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.$textColor || '#f1f5f9'};
    line-height: 1;
  }
  
  .label {
    font-size: 11px;
    color: ${props => props.$labelColor || '#94a3b8'};
    margin-top: 4px;
    white-space: nowrap;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$color || '#f1f5f9'};
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid ${props => props.$color || 'rgba(148, 163, 184, 0.2)'};
  
  .count {
    font-size: 12px;
    padding: 2px 8px;
    background: ${props => props.$color || 'rgba(148, 163, 184, 0.2)'};
    color: white;
    border-radius: 10px;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const ExpirationCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-left: 4px solid ${props => props.$statusColor || '#6b7280'};
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(51, 65, 85, 0.6);
    transform: translateX(4px);
  }
  
  ${props => props.$isUrgent && `
    animation: ${pulse} 2s ease-in-out infinite;
    border-left-width: 6px;
  `}
`;

const TypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${props => props.$bgColor || 'rgba(99, 102, 241, 0.2)'};
  border-radius: 8px;
  font-size: 18px;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 2px;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const DaysLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.$color || '#f1f5f9'};
  
  ${props => props.$isExpired && `
    color: #ef4444;
  `}
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${props => props.$bgColor || 'rgba(107, 114, 128, 0.3)'};
  color: ${props => props.$textColor || '#9ca3af'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  
  button {
    padding: 6px;
    background: transparent;
    border: none;
    color: #64748b;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(71, 85, 105, 0.5);
      color: #e2e8f0;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  
  .icon {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    color: rgba(100, 116, 139, 0.5);
    
    svg {
      width: 100%;
      height: 100%;
    }
  }
  
  h3 {
    font-size: 16px;
    color: #f1f5f9;
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 13px;
    color: #64748b;
    margin: 0;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #64748b;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: rgba(15, 23, 42, 0.98);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  
  h3 {
    margin: 0;
    font-size: 16px;
    color: #f1f5f9;
  }
  
  button {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
    padding: 4px;
    
    &:hover {
      color: #f1f5f9;
    }
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  
  label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #94a3b8;
    margin-bottom: 6px;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 10px 12px;
    background: rgba(15, 23, 42, 0.95);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 6px;
    color: #f1f5f9;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #6366f1;
    }
    
    &::placeholder {
      color: #64748b;
    }
  }
  
  select {
    cursor: pointer;
    
    option {
      background: #0f172a;
      color: #f1f5f9;
      padding: 8px;
    }
  }
  
  /* Style date input calendar icon to be white */
  input[type="date"] {

  }
  
  input[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(1) brightness(1.5);
    cursor: pointer;
    opacity: 1;
  }
  
  input[type="date"]::-webkit-inner-spin-button,
  input[type="date"]::-webkit-clear-button {
    filter: invert(1);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
`;

// ============================================
// ICONS
// ============================================

const Icons = {
  radar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
      <line x1="12" y1="2" x2="12" y2="4"/>
    </svg>
  ),
  add: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,6 5,6 21,6"/>
      <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12"/>
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  report: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  )
};

// Type icons for different expiration types - React components for UI display
const TypeIcons = {
  passport: <FaPassport />,
  i94: <FaClipboardList />,
  visa: <FaIdCard />,
  ead: <FaBriefcase />,
  ap: <FaPlane />,
  medical_exam: <FaHospital />,
  fee_payment: <FaMoneyBillAlt />,
  filing_deadline: <FaCalendarAlt />,
  work_permit: <FaBriefcase />,
  travel_permit: <FaGlobe />,
  status_expiry: <FaClock />,
  rfe_response: <FaEnvelope />,
  appeal_deadline: <FaGavel />,
  biometrics: <FaFingerprint />,
  interview: <FaMicrophone />,
  other: <FaMapPin />
};

// Simple text symbols for dropdown options (since React icons can't render in <option> elements)
const TypeSymbols = {
  passport: '⊡',
  i94: '≡',
  visa: '▣',
  ead: '◈',
  ap: '➤',
  medical_exam: '+',
  fee_payment: '$',
  filing_deadline: '▦',
  work_permit: '◇',
  travel_permit: '◎',
  status_expiry: '◴',
  rfe_response: '✉',
  appeal_deadline: '⚖',
  biometrics: '⊙',
  interview: '◉',
  other: '•'
};

// Status colors
const StatusColors = {
  expired: { bg: 'rgba(153, 27, 27, 0.3)', text: '#fca5a5', border: '#ef4444', label: '#fca5a5' },
  critical: { bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5', border: '#ef4444', label: '#fca5a5' },
  expiring_soon: { bg: 'rgba(245, 158, 11, 0.2)', text: '#fcd34d', border: '#f59e0b', label: '#fcd34d' },
  valid: { bg: 'rgba(16, 185, 129, 0.2)', text: '#6ee7b7', border: '#10b981', label: '#6ee7b7' },
  pending: { bg: 'rgba(107, 114, 128, 0.2)', text: '#9ca3af', border: '#6b7280', label: '#9ca3af' },
  renewed: { bg: 'rgba(99, 102, 241, 0.2)', text: '#a5b4fc', border: '#6366f1', label: '#a5b4fc' }
};

// Immigration deadline types for the dropdown — the existing IRCC list, kept intact.
// Used as the fallback whenever the adviser's category is immigration (catTypes === null).
const IMMIGRATION_EXPIRATION_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'i94', label: 'I-94' },
  { value: 'visa', label: 'Visa' },
  { value: 'ead', label: 'EAD (Employment Authorization)' },
  { value: 'ap', label: 'Advance Parole' },
  { value: 'medical_exam', label: 'Medical Exam' },
  { value: 'fee_payment', label: 'Fee Payment Deadline' },
  { value: 'filing_deadline', label: 'Filing Deadline' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'travel_permit', label: 'Travel Permit' },
  { value: 'status_expiry', label: 'Status Expiry' },
  { value: 'rfe_response', label: 'RFE Response Deadline' },
  { value: 'appeal_deadline', label: 'Appeal Deadline' },
  { value: 'biometrics', label: 'Biometrics Appointment' },
  { value: 'interview', label: 'Interview' },
  { value: 'other', label: 'Other' }
];

// ============================================
// MAIN COMPONENT
// ============================================

const ExpirationRadar = ({ conversationId, clientName }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [tracker, setTracker] = useState(null);
  const [adviserCategory, setAdviserCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpiration, setEditingExpiration] = useState(null);
  const [formData, setFormData] = useState({
    type: 'passport',
    name: '',
    expiryDate: '',
    issueDate: '',
    documentNumber: '',
    notes: '',
    jurisdiction: ''
  });

  // Expiration types for dropdown — personalized per adviser category.
  // Immigration (or unknown-empty) → catTypes is null → keep the existing IRCC list.
  const catTypes = getCategoryDeadlineTypes(adviserCategory);
  const expirationTypes = catTypes
    ? catTypes.map(t => ({ value: t.key, label: t.label }))
    : IMMIGRATION_EXPIRATION_TYPES;

  // Load tracker data
  const loadTracker = useCallback(async () => {
    if (!conversationId) return;
    
    setLoading(true);
    try {
      const response = await authenticatedAxios.get(`/expiration-tracker/${conversationId}`);
      if (response.data.success) {
        setTracker(response.data.tracker);
        setAdviserCategory(response.data.adviserCategory || '');
      }
    } catch (error) {
      console.error('Error loading expiration tracker:', error);
    } finally {
      setLoading(false);
    }
  }, [conversationId, authenticatedAxios]);

  useEffect(() => {
    loadTracker();
  }, [loadTracker]);

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-fill name based on type if empty
    if (field === 'type' && !formData.name) {
      const typeLabel = expirationTypes.find(t => t.value === value)?.label || '';
      setFormData(prev => ({ ...prev, name: typeLabel }));
    }
  };

  // Add new expiration
  const handleAddExpiration = async () => {
    if (!formData.type || !formData.name) {
      showToast('Please fill in required fields', 'warning');
      return;
    }

    try {
      const response = await authenticatedAxios.post(`/expiration-tracker/${conversationId}`, formData);
      if (response.data.success) {
        await loadTracker();
        setShowAddModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error adding expiration:', error);
      showToast('Failed to add expiration. Please try again.', 'error');
    }
  };

  // Update expiration
  const handleUpdateExpiration = async () => {
    if (!editingExpiration) return;

    try {
      const response = await authenticatedAxios.put(
        `/expiration-tracker/${conversationId}/${editingExpiration._id}`,
        formData
      );
      if (response.data.success) {
        await loadTracker();
        setEditingExpiration(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error updating expiration:', error);
      showToast('Failed to update expiration. Please try again.', 'error');
    }
  };

  // Delete expiration
  const handleDeleteExpiration = async (expirationId) => {
    const confirmed = await confirm({
      title: 'Delete Expiration',
      message: 'Are you sure you want to delete this expiration? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (!confirmed) return;

    try {
      const response = await authenticatedAxios.delete(`/expiration-tracker/${conversationId}/${expirationId}`);
      if (response.data.success) {
        await loadTracker();
      }
    } catch (error) {
      console.error('Error deleting expiration:', error);
      showToast('Failed to delete expiration. Please try again.', 'error');
    }
  };

  // Mark as renewed
  const handleMarkRenewed = async (expirationId) => {
    try {
      const response = await authenticatedAxios.put(
        `/expiration-tracker/${conversationId}/${expirationId}`,
        { status: 'renewed' }
      );
      if (response.data.success) {
        await loadTracker();
      }
    } catch (error) {
      console.error('Error marking as renewed:', error);
    }
  };

  // Open edit modal
  const openEditModal = (expiration) => {
    setEditingExpiration(expiration);
    setFormData({
      type: expiration.type,
      name: expiration.name,
      expiryDate: expiration.expiryDate ? new Date(expiration.expiryDate).toISOString().split('T')[0] : '',
      issueDate: expiration.issueDate ? new Date(expiration.issueDate).toISOString().split('T')[0] : '',
      documentNumber: expiration.documentNumber || '',
      notes: expiration.notes || '',
      jurisdiction: expiration.jurisdiction || ''
    });
  };

  // Reset form. Default to the first type of the ACTIVE category list so a
  // non-immigration adviser doesn't start on 'passport'. For immigration the
  // first entry is 'passport', so behavior is unchanged.
  const resetForm = () => {
    setFormData({
      type: expirationTypes[0]?.value || 'other',
      name: '',
      expiryDate: '',
      issueDate: '',
      documentNumber: '',
      notes: '',
      jurisdiction: ''
    });
  };

  // Open the add-deadline modal with a fresh form (valid default type per category).
  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get days until expiry text
  const getDaysText = (expiryDate) => {
    if (!expiryDate) return 'No date';
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  // Group expirations by status
  const groupedExpirations = tracker?.expirations?.reduce((acc, exp) => {
    const status = exp.status || 'pending';
    if (!acc[status]) acc[status] = [];
    acc[status].push(exp);
    return acc;
  }, {}) || {};

  // Sort each group by expiry date
  Object.keys(groupedExpirations).forEach(status => {
    groupedExpirations[status].sort((a, b) => {
      if (!a.expiryDate) return 1;
      if (!b.expiryDate) return -1;
      return new Date(a.expiryDate) - new Date(b.expiryDate);
    });
  });

  const summary = tracker?.summary || {
    expired: 0,
    critical: 0,
    expiringSoon: 0,
    valid: 0,
    pending: 0,
    renewed: 0,
    total: 0
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading expiration data...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderTitle>
          {Icons.radar}
          Expiration & Deadline Radar
        </HeaderTitle>
        <HeaderActions>
          <Button onClick={loadTracker}>
            {Icons.refresh}
            Refresh
          </Button>
          <Button $primary onClick={openAddModal}>
            {Icons.add}
            Add Expiration
          </Button>
        </HeaderActions>
      </Header>

      <SummaryBar>
        <SummaryCard 
          $bgColor="rgba(153, 27, 27, 0.3)"
          $borderColor="rgba(239, 68, 68, 0.5)"
          $textColor="#fca5a5"
          $labelColor="#fca5a5"
        >
          <span className="count">{summary.expired}</span>
          <span className="label">Expired</span>
        </SummaryCard>
        <SummaryCard 
          $bgColor="rgba(239, 68, 68, 0.2)"
          $borderColor="rgba(239, 68, 68, 0.3)"
          $textColor="#f87171"
          $labelColor="#fca5a5"
        >
          <span className="count">{summary.critical}</span>
          <span className="label">Critical</span>
        </SummaryCard>
        <SummaryCard 
          $bgColor="rgba(245, 158, 11, 0.2)"
          $borderColor="rgba(245, 158, 11, 0.3)"
          $textColor="#fbbf24"
          $labelColor="#fcd34d"
        >
          <span className="count">{summary.expiringSoon}</span>
          <span className="label">Expiring Soon</span>
        </SummaryCard>
        <SummaryCard 
          $bgColor="rgba(16, 185, 129, 0.2)"
          $borderColor="rgba(16, 185, 129, 0.3)"
          $textColor="#34d399"
          $labelColor="#6ee7b7"
        >
          <span className="count">{summary.valid}</span>
          <span className="label">Valid</span>
        </SummaryCard>
        <SummaryCard>
          <span className="count">{summary.pending}</span>
          <span className="label">Pending</span>
        </SummaryCard>
      </SummaryBar>

      <Content>
        {tracker?.expirations?.length === 0 ? (
          <EmptyState>
            <div className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <circle cx="12" cy="16" r="2"></circle>
              </svg>
            </div>
            <h3>No Expirations Tracked</h3>
            <p>Start tracking important dates like passport expiry, visa deadlines, and more.</p>
            <Button $primary style={{ marginTop: 16 }} onClick={openAddModal}>
              {Icons.add}
              Add First Expiration
            </Button>
          </EmptyState>
        ) : (
          <>
            {/* Expired items - show first */}
            {groupedExpirations.expired?.length > 0 && (
              <Section>
                <SectionTitle $color="#ef4444">
                  🚨 Expired
                  <span className="count">{groupedExpirations.expired.length}</span>
                </SectionTitle>
                {groupedExpirations.expired.map(exp => (
                  <ExpirationCard 
                    key={exp._id} 
                    $statusColor="#ef4444"
                    $isUrgent
                  >
                    <TypeIcon $bgColor="rgba(239, 68, 68, 0.3)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        Expired {formatDate(exp.expiryDate)}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <DaysLabel $isExpired>{getDaysText(exp.expiryDate)}</DaysLabel>
                      <StatusBadge $bgColor="#991b1b" $textColor="#fca5a5">EXPIRED</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Mark as Renewed" onClick={() => handleMarkRenewed(exp._id)}>
                        {Icons.check}
                      </button>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}

            {/* Critical items */}
            {groupedExpirations.critical?.length > 0 && (
              <Section>
                <SectionTitle $color="#f59e0b">
                  ⚠️ Critical (≤30 days)
                  <span className="count">{groupedExpirations.critical.length}</span>
                </SectionTitle>
                {groupedExpirations.critical.map(exp => (
                  <ExpirationCard key={exp._id} $statusColor="#f59e0b">
                    <TypeIcon $bgColor="rgba(245, 158, 11, 0.3)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        Expires {formatDate(exp.expiryDate)}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <DaysLabel $color="#fbbf24">{getDaysText(exp.expiryDate)}</DaysLabel>
                      <StatusBadge $bgColor="#92400e" $textColor="#fcd34d">CRITICAL</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Mark as Renewed" onClick={() => handleMarkRenewed(exp._id)}>
                        {Icons.check}
                      </button>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}

            {/* Expiring soon */}
            {groupedExpirations.expiring_soon?.length > 0 && (
              <Section>
                <SectionTitle $color="#eab308">
                  ⏰ Expiring Soon (31-90 days)
                  <span className="count">{groupedExpirations.expiring_soon.length}</span>
                </SectionTitle>
                {groupedExpirations.expiring_soon.map(exp => (
                  <ExpirationCard key={exp._id} $statusColor="#eab308">
                    <TypeIcon $bgColor="rgba(234, 179, 8, 0.2)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        Expires {formatDate(exp.expiryDate)}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <DaysLabel $color="#fde047">{getDaysText(exp.expiryDate)}</DaysLabel>
                      <StatusBadge $bgColor="#854d0e" $textColor="#fef08a">SOON</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Mark as Renewed" onClick={() => handleMarkRenewed(exp._id)}>
                        {Icons.check}
                      </button>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}

            {/* Valid items */}
            {groupedExpirations.valid?.length > 0 && (
              <Section>
                <SectionTitle $color="#22c55e">
                  ✅ Valid (90+ days)
                  <span className="count">{groupedExpirations.valid.length}</span>
                </SectionTitle>
                {groupedExpirations.valid.map(exp => (
                  <ExpirationCard key={exp._id} $statusColor="#22c55e">
                    <TypeIcon $bgColor="rgba(34, 197, 94, 0.2)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        Expires {formatDate(exp.expiryDate)}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <DaysLabel $color="#4ade80">{getDaysText(exp.expiryDate)}</DaysLabel>
                      <StatusBadge $bgColor="#166534" $textColor="#86efac">VALID</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}

            {/* Pending items */}
            {groupedExpirations.pending?.length > 0 && (
              <Section>
                <SectionTitle $color="#64748b">
                  📋 Pending (date not set)
                  <span className="count">{groupedExpirations.pending.length}</span>
                </SectionTitle>
                {groupedExpirations.pending.map(exp => (
                  <ExpirationCard key={exp._id} $statusColor="#64748b">
                    <TypeIcon $bgColor="rgba(100, 116, 139, 0.2)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        {exp.notes || 'No date set yet'}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <StatusBadge>PENDING</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}

            {/* Renewed items */}
            {groupedExpirations.renewed?.length > 0 && (
              <Section>
                <SectionTitle $color="#8b5cf6">
                  🔄 Renewed
                  <span className="count">{groupedExpirations.renewed.length}</span>
                </SectionTitle>
                {groupedExpirations.renewed.map(exp => (
                  <ExpirationCard key={exp._id} $statusColor="#8b5cf6">
                    <TypeIcon $bgColor="rgba(139, 92, 246, 0.2)">
                      {TypeIcons[exp.type] || TypeIcons.other}
                    </TypeIcon>
                    <CardContent>
                      <CardTitle>{exp.name}</CardTitle>
                      <CardSubtitle>
                        {exp.documentNumber && `#${exp.documentNumber} • `}
                        Was: {formatDate(exp.expiryDate)}
                      </CardSubtitle>
                    </CardContent>
                    <CardMeta>
                      <StatusBadge $bgColor="#5b21b6" $textColor="#c4b5fd">RENEWED</StatusBadge>
                    </CardMeta>
                    <ActionButtons>
                      <button title="Edit" onClick={() => openEditModal(exp)}>
                        {Icons.edit}
                      </button>
                      <button title="Delete" onClick={() => handleDeleteExpiration(exp._id)}>
                        {Icons.delete}
                      </button>
                    </ActionButtons>
                  </ExpirationCard>
                ))}
              </Section>
            )}
          </>
        )}
      </Content>

      {/* Add/Edit Modal */}
      {(showAddModal || editingExpiration) && (
        <ModalOverlay onClick={() => { setShowAddModal(false); setEditingExpiration(null); resetForm(); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingExpiration ? 'Edit Expiration' : 'Add New Expiration'}</h3>
              <button onClick={() => { setShowAddModal(false); setEditingExpiration(null); resetForm(); }}>
                {Icons.close}
              </button>
            </ModalHeader>
            <ModalContent>
              <FormGroup>
                <label>Type *</label>
                <select 
                  value={formData.type} 
                  onChange={e => handleFormChange('type', e.target.value)}
                >
                  {expirationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {TypeSymbols[type.value] || '•'} {type.label}
                    </option>
                  ))}
                </select>
              </FormGroup>
              <FormGroup>
                <label>Name / Description *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleFormChange('name', e.target.value)}
                  placeholder="e.g., US B1/B2 Visa"
                />
              </FormGroup>
              <FormGroup>
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={e => handleFormChange('expiryDate', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <label>Issue Date (optional)</label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={e => handleFormChange('issueDate', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <label>Document Number (optional)</label>
                <input
                  type="text"
                  value={formData.documentNumber}
                  onChange={e => handleFormChange('documentNumber', e.target.value)}
                  placeholder="e.g., A12345678"
                />
              </FormGroup>
              <FormGroup>
                <label>Jurisdiction (optional)</label>
                <input
                  type="text"
                  value={formData.jurisdiction}
                  onChange={e => handleFormChange('jurisdiction', e.target.value)}
                  placeholder="e.g., US, UK, Canada"
                />
              </FormGroup>
              <FormGroup>
                <label>Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={e => handleFormChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </FormGroup>
            </ModalContent>
            <ModalFooter>
              <Button onClick={() => { setShowAddModal(false); setEditingExpiration(null); resetForm(); }}>
                Cancel
              </Button>
              <Button $primary onClick={editingExpiration ? handleUpdateExpiration : handleAddExpiration}>
                {editingExpiration ? 'Save Changes' : 'Add Expiration'}
              </Button>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ExpirationRadar;

